/**
 * RichComposer — строка ввода мессенджера с видимым форматированием (30.09.2026).
 *
 * Снаружи ведёт себя как textarea с разметкой (**жирный**, _курсив_ …):
 *   • value/onChange(value, selStart, selEnd) — текст С маркерами;
 *   • ref (ComposerHandle): value, selectionStart/End, focus, blur,
 *     setSelectionRange, style, scrollHeight — то подмножество textarea, которое
 *     использует ChatPage (вставка эмодзи/упоминаний по позиции курсора,
 *     авто-рост, панель B/I/U/S).
 * Внутри — contenteditable, который показывает жирный/курсив/… вместо маркеров.
 * Источник правды, пока поле в фокусе, — DOM: после каждого input DOM
 * сериализуется в разметку и уходит наверх; DOM пересобирается из value ТОЛЬКО
 * когда value пришёл извне (черновик, правка сообщения, очистка после отправки,
 * вставка эмодзи/упоминания, кнопки панели) — иначе прыгал бы курсор.
 * Переносы строк: Enter/Shift+Enter → "\n" текстом (white-space: pre-wrap), а не
 * <div>/<br> браузера — сериализация проще и одинакова во всех браузерах.
 */
import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef } from "react";
import type { ClipboardEvent, FormEvent, KeyboardEvent } from "react";
import { domToMarkup, htmlToPlain, markupToDom, setDomSelection } from "@/lib/composerMarkup";

export interface ComposerHandle {
  readonly value: string;
  readonly selectionStart: number;
  readonly selectionEnd: number;
  readonly style: CSSStyleDeclaration;
  readonly scrollHeight: number;
  readonly el: HTMLDivElement | null;
  focus(opts?: FocusOptions): void;
  blur(): void;
  setSelectionRange(start: number, end: number): void;
}

interface Props {
  value: string;
  onChange: (value: string, selStart: number, selEnd: number) => void;
  onSelectionChange?: (hasSelection: boolean) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  onKeyUp?: (e: KeyboardEvent<HTMLDivElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  title?: string;
  className?: string;
  autoFocus?: boolean;
}

/** Вставить текст в позицию курсора с сохранением undo (execCommand жив во всех браузерах). */
function insertText(el: HTMLDivElement, text: string): void {
  el.focus({ preventScroll: true });
  let ok = false;
  try { ok = document.execCommand("insertText", false, text); } catch { ok = false; }
  if (ok) return;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) {
    el.appendChild(document.createTextNode(text));
  } else {
    const r = sel.getRangeAt(0);
    r.deleteContents();
    const tn = document.createTextNode(text);
    r.insertNode(tn);
    r.setStartAfter(tn); r.collapse(true);
    sel.removeAllRanges(); sel.addRange(r);
  }
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

const FMT_SEL = "b, strong, i, em, u, s, strike, del, code, .cf-spoiler";

function caretWrapper(el: HTMLDivElement): HTMLElement | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed || !el.contains(sel.anchorNode)) return null;
  const node = sel.anchorNode as Node;
  const host = node.nodeType === 3 ? node.parentElement : (node as Element);
  if (!host || host === el) return null;
  const w = host.closest(FMT_SEL) as HTMLElement | null;
  return w && el.contains(w) ? w : null;
}

/** После удаления: убрать пустые форматирующие обёртки, каретку — на их место. */
function cleanupAfterDelete(el: HTMLDivElement): void {
  const sel = window.getSelection();
  for (const f of Array.from(el.querySelectorAll<HTMLElement>(FMT_SEL))) {
    if ((f.textContent || "").length > 0) continue;
    const parent = f.parentNode;
    if (!parent) continue;
    const idx = Array.from(parent.childNodes).indexOf(f);
    const caretInside = !!sel && sel.rangeCount > 0 && f.contains(sel.anchorNode);
    parent.removeChild(f);
    if (caretInside && sel) {
      try {
        const r = document.createRange();
        r.setStart(parent, idx);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
      } catch { /* ignore */ }
    }
  }
}

/** Браузер после удаления форматированного текста помнит его стиль и заворачивает
 *  следующий ввод в новый <b>. Если обёртка вокруг каретки содержит только то, что
 *  сейчас ввели, — разворачиваем её (текст остаётся обычным). */
function unwrapFreshWrapper(el: HTMLDivElement, data: string): boolean {
  const w = caretWrapper(el);
  if (!w) return false;
  const t = w.textContent || "";
  if (!t || (data && t.length > data.length)) return false;
  const { selStart } = domToMarkup(el, window.getSelection());
  const parent = w.parentNode;
  if (!parent) return false;
  // длина открывающего маркера снимаемой обёртки: _ ` — 1, ** __ ~~ || — 2
  const tag = w.tagName;
  const openLen = tag === "I" || tag === "EM" || tag === "CODE" ? 1 : 2;
  const frag = document.createDocumentFragment();
  while (w.firstChild) frag.appendChild(w.firstChild);
  parent.replaceChild(frag, w);
  el.normalize();
  const after = domToMarkup(el, null).text;
  const target = Math.max(0, Math.min(after.length, selStart - openLen));
  setDomSelection(el, target, target);
  return true;
}

export const RichComposer = forwardRef<ComposerHandle, Props>(function RichComposer(props, ref) {
  const { value, onSelectionChange, onKeyDown, onKeyUp, onFocus, onBlur, placeholder, title, className, autoFocus } = props;
  const elRef = useRef<HTMLDivElement>(null);
  const lastValueRef = useRef<string>("");
  const propsRef = useRef(props);
  propsRef.current = props;
  const lastInputTypeRef = useRef<string>("");
  const lastInputDataRef = useRef<string>("");
  const plainNextRef = useRef(false);   // после удаления каретка в обычном тексте — набор должен быть обычным

  const rebuild = (text: string) => {
    const el = elRef.current;
    if (!el) return;
    el.replaceChildren(markupToDom(text));
    lastValueRef.current = text;
    if (document.activeElement === el) setDomSelection(el, text.length, text.length);
  };

  // value пришёл извне → пересобрать DOM (свои же изменения уже в DOM).
  useLayoutEffect(() => {
    if (value !== lastValueRef.current) rebuild(value);
  }, [value]);

  const emit = () => {
    const el = elRef.current;
    if (!el) return;
    const it = lastInputTypeRef.current;
    const data = lastInputDataRef.current;
    lastInputTypeRef.current = ""; lastInputDataRef.current = "";
    if (it.startsWith("delete")) {
      cleanupAfterDelete(el);
      plainNextRef.current = caretWrapper(el) === null;
    } else if (it.startsWith("insert") && plainNextRef.current) {
      let n = 0;
      while (n < 3 && unwrapFreshWrapper(el, data)) n++;
      if (n === 0) plainNextRef.current = false;   // вставилось обычным — стиль набора больше не мешает
    }
    // Чужие узлы (картинка из буфера, таблица) — выкинуть, оставив текст
    if (el.querySelector("img, table, a, video, iframe, style, script")) {
      const { text } = domToMarkup(el, null);
      rebuild(text);
    }
    const r = domToMarkup(el, window.getSelection());
    // Пустое поле должно быть пустым и в DOM (Chrome оставляет <br>) — иначе
    // не покажется placeholder (:empty::before)
    if (r.text === "" && el.childNodes.length) el.replaceChildren();
    lastValueRef.current = r.text;
    propsRef.current.onChange(r.text, r.selStart, r.selEnd);
  };

  useImperativeHandle(ref, () => ({
    get value() { return lastValueRef.current; },
    get selectionStart() {
      const el = elRef.current;
      return el ? domToMarkup(el, window.getSelection()).selStart : 0;
    },
    get selectionEnd() {
      const el = elRef.current;
      return el ? domToMarkup(el, window.getSelection()).selEnd : 0;
    },
    get style() { return elRef.current ? elRef.current.style : document.createElement("div").style; },
    get scrollHeight() { return elRef.current ? elRef.current.scrollHeight : 0; },
    get el() { return elRef.current; },
    focus(opts?: FocusOptions) { elRef.current?.focus(opts); },
    blur() { elRef.current?.blur(); },
    setSelectionRange(start: number, end: number) {
      const el = elRef.current;
      if (!el) return;
      setDomSelection(el, start, end);
      propsRef.current.onSelectionChange?.(start !== end);
    },
  }), []);

  // Выделение внутри поля → панель форматирования (selectionchange нет на div как onSelect у textarea)
  useEffect(() => {
    const onSel = () => {
      const el = elRef.current;
      if (!el || document.activeElement !== el) return;
      const s = window.getSelection();
      const has = !!s && s.rangeCount > 0 && !s.isCollapsed && el.contains(s.anchorNode) && el.contains(s.focusNode);
      propsRef.current.onSelectionChange?.(has);
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, []);

  useEffect(() => {
    if (autoFocus) elRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e);
    // Enter, который не забрал родитель (Shift+Enter, Enter на телефоне) — перенос
    // строки текстом, а не <div>/<br> браузера.
    if (e.key === "Enter" && !e.defaultPrevented && elRef.current) {
      e.preventDefault();
      insertText(elRef.current, "\n");
    }
  };
  const handleBeforeInput = (e: FormEvent<HTMLDivElement>) => {
    // Android-клавиатуры шлют insertParagraph без keydown "Enter"
    const it = (e.nativeEvent as InputEvent).inputType;
    if ((it === "insertParagraph" || it === "insertLineBreak") && elRef.current) {
      e.preventDefault();
      insertText(elRef.current, "\n");
    }
  };
  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const cd = e.clipboardData;
    if (!cd || !elRef.current) return;
    for (const it of Array.from(cd.items)) {
      if (it.kind === "file") return;   // файлы — document-уровневый обработчик (handleFiles)
    }
    const plain = cd.getData("text/plain");
    const html = cd.getData("text/html");
    let text = plain;
    if (html) {
      const converted = htmlToPlain(html);
      const plainNL = (plain.match(/\n/g) || []).length;
      const htmlNL = (converted.match(/\n/g) || []).length;
      if (htmlNL > plainNL) text = converted;
    }
    e.preventDefault();
    if (text) insertText(elRef.current, text);
  };

  return (
    <div
      ref={elRef}
      className={(className ?? "") + " chat-form-input--rich"}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label={placeholder}
      data-placeholder={placeholder}
      title={title}
      spellCheck
      onInput={(e) => {
        const ne = e.nativeEvent as InputEvent;
        lastInputTypeRef.current = ne.inputType || "";
        lastInputDataRef.current = ne.data || "";
        emit();
      }}
      onBeforeInput={handleBeforeInput}
      onKeyDown={handleKeyDown}
      onKeyUp={onKeyUp}
      onPaste={handlePaste}
      onFocus={onFocus}
      onBlur={() => { onSelectionChange?.(false); onBlur?.(); }}
    />
  );
});
